// src/utils/csv.js
// All CSV parsing and data analysis utilities
// Every function returns REAL computed results — no stubs

export const parseCSV = (text) => {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;

  // Handle quoted fields
  const splitLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  };

  const headers = splitLine(lines[0]).map(h => h.replace(/"/g, '').trim());
  const rows = lines.slice(1).map(line => {
    const vals = splitLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').replace(/"/g, '').trim(); });
    return obj;
  });

  return { headers, rows, shape: [rows.length, headers.length] };
};

export const inferTypes = (headers, rows) => {
  return headers.map(col => {
    const vals = rows.map(r => r[col]).filter(v => v !== '' && v != null);
    const numVals = vals.filter(v => !isNaN(parseFloat(v)) && isFinite(v));
    const isNum = numVals.length / Math.max(1, vals.length) > 0.8;
    return {
      col,
      type: isNum ? 'numeric' : 'categorical',
      uniqueCount: new Set(vals).size,
      nullCount: rows.length - vals.length,
    };
  });
};

export const getStats = (rows, col) => {
  const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v) && isFinite(v));
  if (!vals.length) return null;
  const sorted = [...vals].sort((a, b) => a - b);
  const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
  const variance = vals.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / vals.length;
  const std = Math.sqrt(variance);
  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const median = sorted[Math.floor(sorted.length * 0.5)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const skew = std > 0 ? (mean - median) / std : 0;
  return { mean, std, min: sorted[0], max: sorted[sorted.length - 1], q1, median, q3, count: vals.length, skew };
};

export const getHistogram = (rows, col, bins = 15) => {
  const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v) && isFinite(v));
  if (!vals.length) return [];
  const mn = Math.min(...vals), mx = Math.max(...vals);
  const binSize = (mx - mn) / bins || 1;
  const counts = Array(bins).fill(0);
  vals.forEach(v => {
    const idx = Math.min(bins - 1, Math.floor((v - mn) / binSize));
    counts[idx]++;
  });
  return counts.map((count, i) => ({
    range: `${(mn + i * binSize).toFixed(2)}`,
    count,
    pct: +((count / vals.length) * 100).toFixed(1),
  }));
};

export const getCorrelation = (rows, c1, c2) => {
  const pairs = rows
    .map(r => [parseFloat(r[c1]), parseFloat(r[c2])])
    .filter(p => !isNaN(p[0]) && !isNaN(p[1]) && isFinite(p[0]) && isFinite(p[1]));
  if (pairs.length < 3) return 0;
  const m1 = pairs.reduce((s, p) => s + p[0], 0) / pairs.length;
  const m2 = pairs.reduce((s, p) => s + p[1], 0) / pairs.length;
  const num = pairs.reduce((s, p) => s + (p[0] - m1) * (p[1] - m2), 0);
  const den = Math.sqrt(
    pairs.reduce((s, p) => s + Math.pow(p[0] - m1, 2), 0) *
    pairs.reduce((s, p) => s + Math.pow(p[1] - m2, 2), 0)
  );
  return den === 0 ? 0 : +(num / den).toFixed(3);
};

export const getValueCounts = (rows, col, limit = 15) => {
  const counts = {};
  rows.forEach(r => { const v = r[col]; if (v !== '' && v != null) counts[v] = (counts[v] || 0) + 1; });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([value, count]) => ({ value, count, pct: +((count / rows.length) * 100).toFixed(1) }));
};

// ── REAL CLEANING OPERATIONS ──
// Returns { rows, log } — actually modifies data

export const dropNullRows = (rows, headers) => {
  const nullCols = headers;
  const before = rows.length;
  const cleaned = rows.filter(row => nullCols.every(col => row[col] !== '' && row[col] != null));
  return {
    rows: cleaned,
    log: `Dropped ${before - cleaned.length} rows with null values. ${cleaned.length} rows remain.`,
  };
};

export const fillWithMean = (rows, colTypes) => {
  const numCols = colTypes.filter(c => c.type === 'numeric');
  const means = {};
  numCols.forEach(ct => {
    const vals = rows.map(r => parseFloat(r[ct.col])).filter(v => !isNaN(v));
    means[ct.col] = vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(4) : '0';
  });
  let filled = 0;
  const newRows = rows.map(row => {
    const newRow = { ...row };
    numCols.forEach(ct => {
      if (newRow[ct.col] === '' || newRow[ct.col] == null || isNaN(parseFloat(newRow[ct.col]))) {
        newRow[ct.col] = means[ct.col];
        filled++;
      }
    });
    return newRow;
  });
  return { rows: newRows, log: `Filled ${filled} null numeric cells with column means.` };
};

export const fillWithMedian = (rows, colTypes) => {
  const numCols = colTypes.filter(c => c.type === 'numeric');
  const medians = {};
  numCols.forEach(ct => {
    const vals = rows.map(r => parseFloat(r[ct.col])).filter(v => !isNaN(v)).sort((a, b) => a - b);
    medians[ct.col] = vals.length ? vals[Math.floor(vals.length / 2)].toFixed(4) : '0';
  });
  let filled = 0;
  const newRows = rows.map(row => {
    const newRow = { ...row };
    numCols.forEach(ct => {
      if (newRow[ct.col] === '' || newRow[ct.col] == null || isNaN(parseFloat(newRow[ct.col]))) {
        newRow[ct.col] = medians[ct.col];
        filled++;
      }
    });
    return newRow;
  });
  return { rows: newRows, log: `Filled ${filled} null numeric cells with column medians.` };
};

export const fillWithMode = (rows, colTypes) => {
  const catCols = colTypes.filter(c => c.type === 'categorical');
  const modes = {};
  catCols.forEach(ct => {
    const counts = {};
    rows.forEach(r => { if (r[ct.col]) counts[r[ct.col]] = (counts[r[ct.col]] || 0) + 1; });
    modes[ct.col] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  });
  let filled = 0;
  const newRows = rows.map(row => {
    const newRow = { ...row };
    catCols.forEach(ct => {
      if (!newRow[ct.col]) { newRow[ct.col] = modes[ct.col]; filled++; }
    });
    return newRow;
  });
  return { rows: newRows, log: `Filled ${filled} null categorical cells with column modes.` };
};

export const removeDuplicates = (rows) => {
  const seen = new Set();
  const cleaned = rows.filter(row => {
    const key = JSON.stringify(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { rows: cleaned, log: `Removed ${rows.length - cleaned.length} duplicate rows. ${cleaned.length} remain.` };
};

export const removeOutliers = (rows, col, multiplier = 1.5) => {
  const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
  const sorted = [...vals].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const iqr = q3 - q1;
  const lo = q1 - multiplier * iqr;
  const hi = q3 + multiplier * iqr;
  const before = rows.length;
  const cleaned = rows.filter(r => {
    const v = parseFloat(r[col]);
    return isNaN(v) || (v >= lo && v <= hi);
  });
  return { rows: cleaned, log: `Removed ${before - cleaned.length} outliers from "${col}" (IQR×${multiplier}). Range: [${lo.toFixed(2)}, ${hi.toFixed(2)}]` };
};

export const normalizeColumn = (rows, col) => {
  const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
  const mn = Math.min(...vals), mx = Math.max(...vals), range = mx - mn || 1;
  const newRows = rows.map(r => {
    const v = parseFloat(r[col]);
    return { ...r, [col]: isNaN(v) ? r[col] : ((v - mn) / range).toFixed(4) };
  });
  return { rows: newRows, log: `Normalized "${col}" to [0,1] range. min=${mn.toFixed(3)}, max=${mx.toFixed(3)}` };
};

export const standardizeColumn = (rows, col) => {
  const vals = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
  const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
  const std = Math.sqrt(vals.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / vals.length) || 1;
  const newRows = rows.map(r => {
    const v = parseFloat(r[col]);
    return { ...r, [col]: isNaN(v) ? r[col] : ((v - mean) / std).toFixed(4) };
  });
  return { rows: newRows, log: `Z-scored "${col}". mean=${mean.toFixed(3)}, std=${std.toFixed(3)}` };
};

// Auto-generate insights from data
export const generateInsights = (dataset, colTypes) => {
  const insights = [];
  const { rows } = dataset;
  const numCols = colTypes.filter(c => c.type === 'numeric');
  const catCols = colTypes.filter(c => c.type === 'categorical');

  insights.push({
    icon: '◈', color: 'var(--cyan)',
    text: `Dataset has ${rows.length.toLocaleString()} rows and ${dataset.headers.length} columns. ${numCols.length} numeric, ${catCols.length} categorical.`,
  });

  const nullCols = colTypes.filter(c => c.nullCount > 0);
  if (nullCols.length > 0) {
    const highNull = nullCols.filter(c => c.nullCount / rows.length > 0.1);
    insights.push({
      icon: '⚠', color: 'var(--gold)',
      text: `${nullCols.reduce((s, c) => s + c.nullCount, 0)} null values across ${nullCols.length} columns.${highNull.length > 0 ? ` High null rate: ${highNull.map(c => c.col).join(', ')}.` : ''}`,
    });
  } else {
    insights.push({ icon: '✓', color: 'var(--green)', text: 'No missing values detected. Dataset is complete.' });
  }

  numCols.slice(0, 4).forEach(ct => {
    const s = getStats(rows, ct.col);
    if (!s) return;
    if (Math.abs(s.skew) > 0.7) {
      insights.push({
        icon: '◉', color: 'var(--violet)',
        text: `"${ct.col}" is ${s.skew > 0 ? 'right' : 'left'}-skewed (skew=${s.skew.toFixed(2)}). Log/sqrt transform may help for ML.`,
      });
    }
    const cv = s.mean !== 0 ? (s.std / Math.abs(s.mean)) : 0;
    if (cv > 1) {
      insights.push({
        icon: '◐', color: 'var(--orange)',
        text: `"${ct.col}" has high coefficient of variation (${(cv * 100).toFixed(0)}%). Consider scaling.`,
      });
    }
  });

  if (numCols.length >= 2) {
    for (let i = 0; i < Math.min(numCols.length, 4); i++) {
      for (let j = i + 1; j < Math.min(numCols.length, 4); j++) {
        const c = getCorrelation(rows, numCols[i].col, numCols[j].col);
        if (Math.abs(c) > 0.8) {
          insights.push({
            icon: '⬡', color: 'var(--red)',
            text: `Very strong correlation (r=${c}) between "${numCols[i].col}" and "${numCols[j].col}". Potential multicollinearity.`,
          });
        }
      }
    }
  }

  return insights;
};
